import React, { useEffect, useState, useRef } from 'react';
import { ChevronRight, ShieldCheck, Monitor, Database, Sparkles, ChevronLeft } from 'lucide-react';

interface HeroProps {
  onNavigate?: (view: 'home' | 'pilot' | 'privacy' | 'terms') => void;
}

export const Hero: React.FC<HeroProps> = ({ onNavigate }) => {
  const [isVisible, setIsVisible] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const BOOKING_URL = "https://outlook.office.com/bookwithme/user/00ab44e829054d0caf6074d9ffaa2153@groflex.ai/meetingtype/0S1TPXwvgk2Nip5dsFYVgQ2?anonymous&ismsaljsauthenabled&ep=mcard";

  const screenshots = [
    { 
      src: "prediction_1.png", 
      label: "AI INSIGHTS: PREDICTIONS",
      detail: "Predictive analytics and intelligent recommendations powered by AI."
    },
    { 
      src: "import_data_1.png", 
      label: "DATA IMPORT ENGINE",
      detail: "Multi-source ingestion including Database, REST API, and PDF files."
    },
    { 
      src: "AI_Insight_1.png", 
      label: "PATTERN ANALYSIS HUB",
      detail: "Visualizing data trends and interdependencies across operational silos."
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollPos = scrollRef.current.scrollLeft;
      const width = scrollRef.current.offsetWidth;
      const index = Math.round(scrollPos / width);
      setActiveSlide(index);
    }
  };

  const scrollToSlide = (index: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: index * scrollRef.current.offsetWidth,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section className="relative pt-40 pb-24 lg:pt-52 lg:pb-40 px-6 md:px-12 bg-brandBg overflow-hidden">
      {/* Background Aesthetic Layer */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.03]">
          <svg viewBox="0 0 500 800" className="w-full h-full text-zodiac">
            <circle cx="400" cy="200" r="350" fill="none" stroke="currentColor" strokeWidth="0.5" strokeDasharray="12 12" />
            <circle cx="400" cy="200" r="200" fill="none" stroke="currentColor" strokeWidth="1" />
            <line x1="0" y1="200" x2="500" y2="200" stroke="currentColor" strokeWidth="0.5" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-8 items-center">
          
          {/* Left Column: Strategic Messaging */}
          <div className={`lg:col-span-5 transition-all duration-1000 transform ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
            
            <div className="inline-flex items-center space-x-3 mb-8 py-2 px-4 rounded-full bg-zodiac/5 border border-zodiac/10">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse"></span>
              <span className="text-[10px] uppercase tracking-[0.3em] font-black text-zodiac/60">Business Operations Intelligence</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-zodiac leading-[1.05] mb-8 tracking-tighter">
              Prevent Operational Failures <span className="text-accent">Before</span> They Happen.
            </h1>
            
            <p className="text-xl text-zodiac/60 max-w-xl mb-12 leading-relaxed font-light">
              Groflex governs how critical decisions turn into coordinated action across your existing systems so teams act in time, stay aligned, and avoid costly execution breakdowns.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-start space-y-6 sm:space-y-0 sm:space-x-8 mb-16">
              <button 
                onClick={() => window.open(BOOKING_URL, '_blank')}
                className="w-full sm:w-auto bg-zodiac text-white px-10 py-5 rounded font-bold text-xs uppercase tracking-widest hover:bg-accent transition-executive shadow-[0_20px_40px_rgba(17,34,72,0.15)] hover:scale-105 active:scale-95 flex items-center justify-center gap-3 group"
              >
                BOOK A CALL
              </button>
              <button 
                onClick={() => onNavigate?.('pilot')}
                className="group flex items-center space-x-2 text-zodiac font-bold text-xs uppercase tracking-[0.2em] hover:text-accent transition-executive"
              >
                <span>APPLY FOR A PILOT</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Platform Badge */}
            <div className="flex items-center space-x-4 pt-8 border-t border-zodiac/5">
              <ShieldCheck className="w-5 h-5 text-accent/40" />
              <span className="text-[10px] font-black uppercase tracking-[0.4em] text-zodiac/30">Enterprise Grade Platform</span>
            </div>
          </div>

          {/* Right Column: Platform UI Gallery Stage */}
          <div className={`lg:col-span-7 transition-all duration-1000 delay-300 transform ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
            <div className="relative group lg:scale-105 lg:translate-x-6">
              {/* Outer Ambiance */}
              <div className="absolute -inset-6 bg-accent/5 blur-3xl rounded-[3rem] opacity-40 group-hover:opacity-60 transition-opacity duration-1000"></div>
              
              {/* Device Frame */}
              <div className="relative bg-zodiac rounded-[2.5rem] lg:rounded-[3rem] shadow-[0_60px_100px_rgba(17,34,72,0.25)] overflow-hidden border border-white/10">
                
                {/* Header Context Bar */}
                <div className="h-14 border-b border-white/5 bg-white/5 backdrop-blur-xl flex items-center justify-between px-8 relative z-20">
                  <div className="flex items-center gap-4">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/20"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/20"></div>
                    </div>
                    <div className="h-4 w-px bg-white/10 mx-2"></div>
                    <div className="flex items-center gap-2">
                      <Monitor className="w-3.5 h-3.5 text-accent/40" />
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white/30">Groflex Environment Live</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                       <div className="w-1 h-1 rounded-full bg-green-500 shadow-[0_0_8px_#22c55e]"></div>
                       <span className="text-[8px] font-black uppercase tracking-widest text-white/20">Sync Active</span>
                    </div>
                  </div>
                </div>

                {/* Scrollable Screen Stage */}
                <div 
                  ref={scrollRef}
                  onScroll={handleScroll}
                  className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar relative z-10 scroll-smooth"
                >
                  {screenshots.map((screen, idx) => (
                    <div key={idx} className="min-w-full aspect-[16/10] snap-start relative overflow-hidden group/screen">
                      <img 
                        src={screen.src} 
                        alt={screen.label} 
                        className="w-full h-full object-cover transition-transform duration-[10s] group-hover/screen:scale-105"
                      />
                      {/* Darkening Overlay for Content Readability */}
                      <div className="absolute inset-0 bg-gradient-to-t from-zodiac via-transparent to-transparent opacity-60"></div>
                      
                      {/* Context Overlay */}
                      <div className="absolute bottom-8 left-8 right-8">
                         <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/20 border border-accent/40 rounded-full mb-3">
                            <Sparkles className="w-3 h-3 text-accent" />
                            <span className="text-[8px] font-black uppercase tracking-widest text-white">{screen.label}</span>
                         </div>
                         <p className="text-white/60 text-xs font-medium max-w-xs">{screen.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Controls */}
                <div className="absolute bottom-6 right-8 z-30 flex items-center gap-6">
                  <div className="flex gap-2">
                    {screenshots.map((_, i) => (
                      <button 
                        key={i}
                        onClick={() => scrollToSlide(i)}
                        className={`h-1 rounded-full transition-all duration-500 ${activeSlide === i ? 'w-8 bg-accent' : 'w-2 bg-white/10 hover:bg-white/30'}`}
                      />
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => scrollToSlide(activeSlide - 1)}
                      disabled={activeSlide === 0}
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20 transition-all"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => scrollToSlide(activeSlide + 1)}
                      disabled={activeSlide === screenshots.length - 1}
                      className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-20 transition-all"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Interface Metadata Decorative Element */}
                {/* <div className="absolute top-20 right-8 z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                   <div className="p-4 bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-2xl flex flex-col items-end">
                      <div className="flex items-center gap-2 mb-2">
                         <Database className="w-3 h-3 text-accent/50" />
                         <span className="text-[8px] font-black uppercase tracking-widest text-white/40">Ingested Nodes</span>
                      </div>
                      <span className="text-xl font-black text-white tracking-tighter">1,240</span>
                   </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};